import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Rocket, Send, Save, Eye } from "lucide-react";

const TOKEN_REFERENCE = [
  ["{{name}}", "Recipient's display name"],
  ["{{pub_name}}", "The pub the recipient signed up under"],
  ["{{first_match_date}}", "First upcoming match date (e.g. 'Sat 24 Aug 2026')"],
];

const SAMPLE_TOKENS = {
  name: "Sam",
  pub_name: "The Founders Arms",
  first_match_date: "Sat 24 Aug 2026",
};

function tinyMarkdownToHtml(md) {
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const paragraphs = md.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return paragraphs
    .map((p) => {
      let h = escape(p).replace(/\n/g, "<br>");
      h = h.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#0038FF;text-decoration:underline;">$1</a>');
      return `<p style="margin:0 0 12px 0;line-height:1.55">${h}</p>`;
    })
    .join("");
}

function renderTokens(text, tokens) {
  let out = text;
  for (const [k, v] of Object.entries(tokens)) {
    out = out.split(`{{${k}}}`).join(String(v));
  }
  return out;
}

export default function SeasonKickoffCard() {
  const { user: currentUser } = useAuth();
  const [tpl, setTpl] = useState({ subject: "", body_markdown: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates/season_kickoff");
      setTpl({ subject: data.subject, body_markdown: data.body_markdown });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/email-templates/season_kickoff", {
        subject: tpl.subject,
        body_markdown: tpl.body_markdown,
      });
      toast.success("Season Kick-Off template saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const send = async (mode) => {
    if (mode === "all" && !window.confirm(
      "Send the Season Kick-Off email to EVERY player with a pub slug " +
      "(excluding opted-out and Global users)?"
    )) return;
    setSending(true);
    try {
      const payload = mode === "test"
        ? { test_to: currentUser?.email }
        : {};
      const { data } = await api.post("/admin/email-blasts/season-kickoff", payload);
      const summary = mode === "test"
        ? `Test sent to ${currentUser?.email} — ${data.sent} sent, ${data.failed} failed`
        : `Blast complete · ${data.sent} sent · ${data.failed} failed · ${data.skipped} skipped`;
      toast.success(summary);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const previewSubject = renderTokens(tpl.subject, SAMPLE_TOKENS);
  const previewBody = renderTokens(tpl.body_markdown, SAMPLE_TOKENS);

  return (
    <section
      className="border border-zinc-200 rounded-sm bg-white p-6 mt-12"
      data-testid="season-kickoff-card"
    >
      <header className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Rocket className="h-3.5 w-3.5" /> Season Kick-Off blast
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            Fire the starter pistol for the new season.
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Personalised email to every player with a pub slug — Global users, opt-outs and bots are excluded.
            Each recipient gets a branded CTA that opens straight into their own <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">/c/&lt;slug&gt;/matches</code>.
          </p>
        </div>
      </header>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor side */}
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Subject</Label>
              <Input
                value={tpl.subject}
                onChange={(e) => setTpl((s) => ({ ...s, subject: e.target.value }))}
                data-testid="kickoff-subject"
                className="mt-1 h-10 rounded-sm border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Body (Markdown)</Label>
              <Textarea
                value={tpl.body_markdown}
                onChange={(e) => setTpl((s) => ({ ...s, body_markdown: e.target.value }))}
                data-testid="kickoff-body"
                rows={12}
                className="mt-1 rounded-sm border-zinc-300 font-mono text-xs"
              />
            </div>

            <div className="text-[11px] text-zinc-500">
              <p className="font-bold uppercase tracking-[0.18em] mb-1">Available tokens</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                {TOKEN_REFERENCE.map(([token, desc]) => (
                  <li key={token} className="truncate">
                    <code className="text-[10px] bg-zinc-100 px-1 py-0.5 rounded">{token}</code> — {desc}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
              <Button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="kickoff-save-template"
                className="h-10 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold"
              >
                <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save template"}
              </Button>
              <Button
                type="button"
                onClick={() => send("test")}
                disabled={sending || !currentUser?.email}
                variant="outline"
                data-testid="kickoff-send-test"
                className="h-10 rounded-sm"
                title={currentUser?.email ? `Sends to ${currentUser.email}` : "Sign in required"}
              >
                <Eye className="h-4 w-4 mr-1.5" /> Send test to admin
              </Button>
              <Button
                type="button"
                onClick={() => send("all")}
                disabled={sending}
                data-testid="kickoff-send-all"
                className="h-10 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold"
              >
                <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : "Send to all"}
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="kickoff-preview">
            <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Preview · sample tokens</p>
              <p className="text-sm font-bold tracking-tight mt-1 truncate" data-testid="kickoff-preview-subject">
                {previewSubject || "(no subject)"}
              </p>
            </div>
            <div className="p-4">
              <div
                className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-amber-900 bg-amber-200 rounded px-2 py-1 mb-3"
                data-testid="kickoff-preview-first-match"
              >
                First fixture · {SAMPLE_TOKENS.first_match_date}
              </div>
              <div
                className="prose prose-sm max-w-none text-zinc-700"
                data-testid="kickoff-preview-body"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(tinyMarkdownToHtml(previewBody), {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "a"],
                    ALLOWED_ATTR: ["href", "style", "target", "rel"],
                  }),
                }}
              />
              <div className="mt-4">
                <span
                  data-testid="kickoff-preview-cta"
                  className="inline-block text-white font-semibold text-sm px-4 py-2.5 rounded-sm bg-[#0038FF]"
                >
                  Get me to my matches →
                </span>
                <p className="mt-2 text-[11px] text-zinc-500">
                  Real recipients: link opens{" "}
                  <code className="bg-zinc-100 px-1 py-0.5 rounded">/c/&lt;their-slug&gt;/matches</code>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
